import React from 'react';
import { Mission, MissionStatus } from '../../../types/mission';
import { UserProgress } from '../../../types/progress';
import { MissionCard } from '../../mission';
import styles from './MissionList.module.css';

/**
 * Props for the MissionList component
 */
interface MissionListProps {
  missions: Mission[];
  userProgress?: UserProgress; // Make userProgress optional
  onSelectMission: (missionId: string) => void;
}

/**
 * Default user progress for when none is provided
 */
const defaultUserProgress: UserProgress = {
  currentStage: 1,
  completedMissions: [],
  skillLevels: {
    coordinates: 1,
    basicMoves: 1,
    sequences: 1,
    gameAnalysis: 1
  },
  statistics: {
    totalMissionsCompleted: 0,
    totalChallengesCompleted: 0,
    averageAccuracy: 0,
    bestScore: 0,
    lastActive: new Date().toISOString()
  }
};

/**
 * Determines the status of a mission based on user progress
 * 
 * @param mission The mission to check
 * @param userProgress The user's progress data
 * @returns The mission's status (locked, available, or completed)
 */
const getMissionStatus = (mission: Mission, userProgress: UserProgress = defaultUserProgress): MissionStatus => {
  // Check if mission is completed
  if (userProgress.completedMissions?.includes(mission.id)) {
    return 'completed';
  }
  
  // Check if mission is locked (prerequisites not met)
  const hasUncompletedPrerequisites = mission.prerequisites?.some(
    prereqId => !userProgress.completedMissions?.includes(prereqId)
  );
  
  // Also check if mission stage is higher than current stage
  const isHigherStage = mission.stage > (userProgress.currentStage || 1);
  
  if (hasUncompletedPrerequisites || isHigherStage) {
    return 'locked';
  }
  
  // Otherwise, mission is available
  return 'available';
};

/**
 * MissionList component
 * 
 * Displays a list of missions with their status based on user progress
 */
const MissionList: React.FC<MissionListProps> = ({ 
  missions = [], // Provide default empty array to avoid undefined
  userProgress = defaultUserProgress, 
  onSelectMission 
}) => {
  // Group missions by stage (with safety check)
  const missionsByStage = (missions || []).reduce<Record<number, Mission[]>>((acc, mission) => {
    if (!acc[mission.stage]) {
      acc[mission.stage] = [];
    }
    acc[mission.stage].push(mission);
    return acc;
  }, {});
  
  // Sort stages in ascending order
  const stages = Object.keys(missionsByStage).map(Number).sort((a, b) => a - b);
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Available Missions</h2>
        <div className={styles.stageIndicator}>
          Current Stage: <span className={styles.stageValue}>{userProgress.currentStage || 1}</span>
        </div>
      </div>
      
      {stages.map(stage => (
        <div key={stage} className={styles.stageSection}>
          <h3 className={styles.stageTitle}>
            Stage {stage}
            {stage === userProgress.currentStage && (
              <span className={styles.currentStage}>(Current)</span>
            )}
          </h3>
          
          <div className={styles.missionGrid}>
            {missionsByStage[stage].map(mission => (
              <div key={mission.id} className={styles.missionCardWrapper}>
                <MissionCard
                  mission={mission}
                  status={getMissionStatus(mission, userProgress)}
                  onClick={() => onSelectMission(mission.id)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {(!missions || missions.length === 0) && (
        <div className={styles.emptyState}>
          <p>No missions available. Check back later for new assignments.</p>
        </div>
      )}
    </div>
  );
};

export default MissionList; 